import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { liveTickerData } from "@/mocks/homepageData";
import { useScrollReveal, getRevealClasses } from "@/hooks/useScrollReveal";
import { brokers } from "@/data/brokers";

export default function HeroSection() {
  const [tickerIndex, setTickerIndex] = useState(0);
  const { ref: heroRef, isVisible: heroVisible } = useScrollReveal(0.05);

  // Real-time EUR/USD mini chart — accumulates price points over time
  const [forexRates, setForexRates] = useState<number[]>([]);
  const [forexChange, setForexChange] = useState("+0.00%");
  const [forexCurrent, setForexCurrent] = useState("1.0850");
  const priceHistoryRef = useRef<number[]>([]);
  const maxBars = 20;

  useEffect(() => {
    const fetchLatestRate = async () => {
      try {
        const urls = [
          "https://api.frankfurter.dev/v1/latest?from=EUR&to=USD",
          "https://open.er-api.com/v6/latest/EUR",
        ];

        for (const url of urls) {
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 8000);
            const res = await fetch(url, { signal: controller.signal });
            clearTimeout(timeoutId);

            if (!res.ok) continue;
            const json = await res.json();
            const rates: Record<string, number> = json.rates ?? {};
            const usdRate = rates.USD;
            if (!usdRate || usdRate <= 0) continue;

            // Accumulate history
            const history = priceHistoryRef.current;
            history.push(usdRate);
            if (history.length > maxBars) history.shift();

            if (history.length >= 2) {
              const first = history[0];
              const changePct = ((usdRate - first) / first) * 100;
              const sign = changePct >= 0 ? "+" : "";
              setForexChange(`${sign}${changePct.toFixed(2)}%`);
            }

            setForexCurrent(usdRate.toFixed(4));
            setForexRates([...history]);
            return; // success, stop trying other URLs
          } catch {
            // try next URL
          }
        }
      } catch {
        // all URLs failed, silently keep previous data
      }
    };

    fetchLatestRate();
    const interval = setInterval(fetchLatestRate, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setTickerIndex((prev) => (prev + 1) % liveTickerData.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Normalize rates to 0-100% height for chart bars
  const normalizedRates = (() => {
    if (forexRates.length === 0) return [];
    const min = Math.min(...forexRates);
    const max = Math.max(...forexRates);
    const range = max - min || 1;
    return forexRates.map((r) => ((r - min) / range) * 100);
  })();

  return (
    <section className="relative min-h-[100vh] flex items-center overflow-hidden bg-[#111827]">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-[#7C3AED]/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#10B981]/5 rounded-full blur-[100px]" />
      </div>

      <div ref={heroRef} className={`relative max-w-7xl mx-auto px-4 md:px-6 py-24 md:py-32 w-full ${getRevealClasses(heroVisible, "up")}`}>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Column - Typography */}
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#7C3AED]/40 bg-[#7C3AED]/10 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse"></span>
              <span className="text-sm text-[#F8FAFC]">Live Performance Data 2026</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#F8FAFC] leading-tight mb-6">
              Is{" "}
              <span className="text-gradient">XM Broker</span>{" "}
              Safe? 2026 In-Depth Review & Live Performance Test
            </h1>

            <p className="text-lg md:text-xl text-[#94A3B8] leading-relaxed mb-8">
              Discover why millions of global traders choose XM. Get unbiased data on spreads, execution speed, and hidden costs.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={brokers.xm.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#7C3AED] hover:bg-[#8B5CF6] text-white font-semibold rounded-lg transition-all hover:shadow-xl hover:shadow-[#7C3AED]/25 group whitespace-nowrap"
              >
                Open XM Account
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </a>
              <Link
                to="/rankings"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#374151] hover:border-[#7C3AED] text-[#F8FAFC] font-semibold rounded-lg transition-all hover:bg-[#1F2937] whitespace-nowrap"
              >
                Platform Rankings
                <i className="ri-bar-chart-line"></i>
              </Link>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center gap-4 mt-10 text-sm text-[#64748B]">
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line text-[#10B981]"></i>
                <span>ASIC Regulated</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-user-star-line text-[#10B981]"></i>
                <span>5M+ Traders</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-award-line text-[#10B981]"></i>
                <span>4.8/5 Rating</span>
              </div>
            </div>
          </div>

          {/* Right Column - Live Terminal Dashboard */}
          <div className="relative">
            <div className="bg-[#1F2937] rounded-2xl border border-[#374151] overflow-hidden shadow-2xl shadow-black/40">
              {/* Terminal Header */}
              <div className="flex items-center gap-2 px-5 py-4 border-b border-[#374151] bg-[#0B0F19]">
                <div className="flex gap-2">
                  <span className="w-3 h-3 rounded-full bg-[#EF4444]"></span>
                  <span className="w-3 h-3 rounded-full bg-[#F59E0B]"></span>
                  <span className="w-3 h-3 rounded-full bg-[#10B981]"></span>
                </div>
                <span className="ml-3 text-xs text-[#64748B] font-mono">xm-live-terminal — bash — 80x24</span>
              </div>

              {/* Terminal Body */}
              <div className="p-5 space-y-4">
                <div className="flex items-center gap-2 text-xs text-[#64748B] font-mono mb-4">
                  <span className="text-[#10B981]">➜</span>
                  <span>~</span>
                  <span className="text-[#94A3B8]">xm-review --live-data --broker=XM</span>
                </div>

                {/* Rotating ticker highlight */}
                <div className="bg-[#0B0F19] rounded-xl p-5 border border-[#374151]/50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-[#64748B] uppercase tracking-wider font-mono">Live Metric</span>
                    <span className="flex items-center gap-1.5 text-xs text-[#10B981]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse"></span>
                      Real-time
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <div>
                      <div className="text-sm text-[#94A3B8] mb-1">{liveTickerData[tickerIndex].label}</div>
                      <div className="text-3xl font-bold text-[#F8FAFC]">{liveTickerData[tickerIndex].value}</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-medium ${liveTickerData[tickerIndex].positive ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                        {liveTickerData[tickerIndex].positive ? "▲" : "▼"} {liveTickerData[tickerIndex].change}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stats grid */}
                <div className="grid grid-cols-2 gap-3">
                  {liveTickerData.map((item, idx) => (
                    <div
                      key={item.label}
                      className={`p-3 rounded-lg border transition-all ${
                        idx === tickerIndex
                          ? "border-[#7C3AED]/50 bg-[#7C3AED]/5"
                          : "border-[#374151]/50 bg-[#0B0F19]"
                      }`}
                    >
                      <div className="text-xs text-[#64748B] mb-1">{item.label}</div>
                      <div className="text-sm font-semibold text-[#F8FAFC]">{item.value}</div>
                    </div>
                  ))}
                </div>

                {/* Mini chart area — real ECB data */}
                <div className="bg-[#0B0F19] rounded-lg p-4 border border-[#374151]/50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-[#64748B] font-mono">EURUSD — Live</span>
                    <div className="flex items-center gap-2">
                      {forexCurrent !== "1.0850" && (
                        <span className="text-xs text-[#94A3B8] font-mono">{forexCurrent}</span>
                      )}
                      <span className={`text-xs ${forexChange.startsWith("+") ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                        {forexChange}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-end gap-1 h-16">
                    {normalizedRates.length > 0
                      ? normalizedRates.map((h, i) => {
                          const lastIdx = normalizedRates.length - 1;
                          const isUp = i === 0 || normalizedRates[i] >= normalizedRates[i - 1];
                          const isLast = i === lastIdx;
                          return (
                            <div
                              key={i}
                              className={`flex-1 rounded-sm transition-colors ${
                                isLast
                                  ? "bg-[#7C3AED]"
                                  : isUp
                                    ? "bg-[#10B981]/40 hover:bg-[#10B981]/70"
                                    : "bg-[#EF4444]/40 hover:bg-[#EF4444]/70"
                              }`}
                              style={{ height: `${Math.max(4, h)}%` }}
                            />
                          );
                        })
                      : [40, 55, 45, 60, 50, 70, 65, 80, 75, 90, 85, 95, 88, 92, 98, 94, 100, 96, 102, 99].map((h, i) => (
                          <div
                            key={i}
                            className="flex-1 rounded-sm bg-[#7C3AED]/40 hover:bg-[#7C3AED]/70 transition-colors"
                            style={{ height: `${h}%` }}
                          />
                        ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}